import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { AxiosResponse } from 'axios';
import { selectAuthToken } from 'selectors';
import { useContextHeaders } from 'hooks/useContextHeaders';
import { useNotification } from 'hooks/useNotification';
import { updateTeamApi, UpdateTeamPayload, UpdateTeamResponse } from 'TeamModule/api/updateTeamApi';
import { teamQueryKeys } from '../teamQueryKeys';

export const useUpdateTeamMutation = () => {
  const token = useSelector(selectAuthToken);
  const queryClient = useQueryClient();
  const { buildConfig } = useContextHeaders();
  const { showSuccess, showError } = useNotification();

  const mutation = useMutation<AxiosResponse<UpdateTeamResponse>, unknown, UpdateTeamPayload>({
    mutationFn: async (params) => {
      if (!token) throw new Error('No auth token available');
      return updateTeamApi({
        params,
        token,
        config: buildConfig(),
      }) as Promise<AxiosResponse<UpdateTeamResponse>>;
    },
    onSuccess: () => {
      showSuccess('Team updated');
      void queryClient.invalidateQueries({
        queryKey: teamQueryKeys.lists(),
      });
    },
    onError: (error) => {
      // Errors can arrive as:
      //  - AxiosResponse (4xx — rejected by handleResponse with the raw
      //    response; server body is on `.data`)
      //  - ApiError (5xx / network — handleError populates `.message`,
      //    `.response.data`, and an optional `.data`)
      //  - plain Error (e.g. thrown "No auth token available")
      const e = error as {
        data?: { message?: string } | unknown;
        response?: { data?: { message?: string } | unknown };
        message?: string;
      };
      const readMessage = (value: unknown): string | undefined => {
        if (value && typeof value === 'object' && 'message' in value) {
          const m = (value as { message?: unknown }).message;
          return typeof m === 'string' ? m : undefined;
        }
        return undefined;
      };
      const message =
        readMessage(e.data) ||
        readMessage(e.response?.data) ||
        (typeof e.message === 'string' ? e.message : undefined) ||
        'Failed to update team';
      showError(message);
    },
  });

  return {
    updateTeam: mutation.mutate,
    updateTeamAsync: mutation.mutateAsync,
    isUpdatingTeam: mutation.isPending,
  };
};
